import { get } from 'lodash';
import { OktaAuth, OktaAuthOptions } from '@okta/okta-auth-js';

export interface DynamicConfig {
    environment: 'prod' | 'uat' | 'test' | 'dev' | 'local';
    isProduction: string;
    clientId: string;
    redirectUri: string;
    maxClockSkew: number;
    issuer: string;
    accountConnectBaseUrl: string;
    myLoanBaseUrl: string;
    accountConnectProfile: string;
    purchaseWidgetLink: string;
    cashOutWidgetLink: string;
    activityUpdateUrl: string;
    sessionIdleTime: string;
    sessionTimeout: string;
    awsProjectRegion: string;
    awsCognitoIdentityPoolId: string;
    awsCognitoRegion: string;
    awsMobileAnalyticsAppId: string;
    awsMobileAnalyticsAppRegion: string;
    gtmId: string;
    authorizeUrl: string;
}

export const defaultConfig: DynamicConfig = {
    environment: 'prod',
    isProduction: 'true',
    clientId: '',
    redirectUri: window.location.origin,
    issuer: 'https://freedommortgage.com/oauth2/default',
    maxClockSkew: 3600,
    accountConnectBaseUrl: 'https://myaccountaws.freedommortgage.com',
    authorizeUrl: 'https://myaccountaws.freedommortgage.com/mtg/loan/fhmc/site2/HomepageV2/login',
    myLoanBaseUrl: 'https://myloan.freedommortgage.com',
    accountConnectProfile: 'https://myaccountaws.freedommortgage.com/mtg/myloan/fhmc/account/profile',
    sessionIdleTime: '25',
    sessionTimeout: '7',
    gtmId: 'GTM-5FXP79Z',
    purchaseWidgetLink:
        'https://mymortgage.freedommortgage.com/prequalify/?ex-25=2&utm_source=myfreedom&utm_medium=get-prequalified-cta&utm_campaign=purchase-prequal-web',
    cashOutWidgetLink:
        'https://www.freedommortgage.com/cash-out-refinance?utm_source=myfreedom&utm_medium=get-the-details-cta&utm_campaign=cash-out-refi-web',
    activityUpdateUrl: 'https://gateway-test.freedommortgage.com/api/v1/selfserv/customers/saveEvents',
    awsProjectRegion: 'us-east-1',
    awsCognitoIdentityPoolId: '',
    awsCognitoRegion: 'us-east-1',
    awsMobileAnalyticsAppId: '',
    awsMobileAnalyticsAppRegion: 'us-east-1'
};

class OktaConfig {
    oktaAuth: OktaAuth | undefined;
    isNotDefined = true;

    public getOkta(): OktaAuth {
        if (this.isNotDefined || !this.oktaAuth) {
            throw new Error('Okta Config is not yet initialized yet.');
        } else {
            return this.oktaAuth;
        }
    }

    public setOkta(instance: OktaAuth) {
        if (this.isNotDefined) {
            this.oktaAuth = instance;
            this.isNotDefined = false;
        } else {
            throw new Error('Okta Config is has already been defined and now has been called second time. This is probably not intended.');
        }
    }

    //This is only for TestUtils to setOkta instance
    public setIsNotDefined(isNotDefined: boolean) {
        this.isNotDefined = isNotDefined;
    }
}

class GlobalConfig {
    environment: DynamicConfig = defaultConfig;
    notDefinedYet = true;

    public get(): DynamicConfig {
        if (this.notDefinedYet) {
            throw new Error(
                'Global config has not been defined yet. Be sure to call the getter only after the config has been downloaded and set. Probable cause is accessing globalConfig in static context.'
            );
        } else {
            return this.environment;
        }
    }

    public env(key: string): string {
        if (this.notDefinedYet) {
            throw new Error(
                'Global config has not been defined yet. Be sure to call the getter only after the config has been downloaded and set. Probable cause is accessing globalConfig in static context.'
            );
        } else {
            return get(this.environment, key);
        }
    }

    public async set(value: DynamicConfig): Promise<boolean> {
        if (this.notDefinedYet) {
            this.environment = value;
            this.environment.redirectUri = window.location.origin;
            this.notDefinedYet = false;
            return true;
        } else {
            throw new Error('Global config has already been defined and now has been called second time. This is probably not intended.');
        }
    }
}

class FullAppConfig {
    private attributes: any = {};
    private isDefined = false;

    public getAttributes() {
        if (this.isDefined) {
            return this.attributes;
        } else throw new Error('Full app attributes are not defined yet. Please set the attributes first.');
    }

    public setAttributes(value: Record<string, any>) {
        if (typeof value === 'object') {
            this.isDefined = true;
            this.attributes = value;
        } else throw new Error('Please provide the values in Object format');
    }
}

export const config = new GlobalConfig();

export const auth = new OktaConfig();

export const fullAppInstance = new FullAppConfig();
